import { useEffect, useMemo, useRef, useState } from "react";
import { getSavedSearchNewCompanyIdsApi } from "../../api/homeApi";
import {
  NEW_RESULT_ROW_FLASH_MS,
  prioritizeNewCompanies,
  readFocusNewResultsState,
  resolveNewCompanyIds,
} from "../../helper/savedSearchNewResults";

export const NEW_RESULTS_IDLE = "idle";
export const NEW_RESULTS_LOADING = "loading";
export const NEW_RESULTS_AVAILABLE = "available";
export const NEW_RESULTS_UNAVAILABLE = "unavailable";

function initialFocus(entry) {
  if (!entry) return { status: NEW_RESULTS_IDLE, ids: [] };
  if (entry.newCompanyIds.length > 0) return { status: NEW_RESULTS_AVAILABLE, ids: entry.newCompanyIds };
  return { status: NEW_RESULTS_LOADING, ids: [] };
}

/**
 * Home "+N new" focus on the Saved Search page (Spec §7, R8).
 *
 * The entry is read once, on the first render. When it carries only a count,
 * the ids come from `GET /home/saved-search-updates/{id}/new-company-ids`.
 * While the server reports `unavailable` (corpusAddedAt not indexed yet,
 * ETL #93) nothing is highlighted. Opening the page does not mark anything
 * seen; the visit end does that (useSavedSearchVisit).
 */
export default function useSavedSearchNewResults({ savedSearchId, entryState, companies, ready }) {
  const entryRef = useRef(undefined);
  if (entryRef.current === undefined) entryRef.current = readFocusNewResultsState(entryState);
  const [focus, setFocus] = useState(() => initialFocus(entryRef.current));
  const [flashNewResultRows, setFlashNewResultRows] = useState(false);
  const flashedRef = useRef(false);

  useEffect(() => {
    if (focus.status !== NEW_RESULTS_LOADING || !savedSearchId) return undefined;
    let cancelled = false;
    Promise.resolve(getSavedSearchNewCompanyIdsApi(savedSearchId)).then((response) => {
      if (cancelled) return;
      const data = response?.status === 200 ? response.data : null;
      setFocus(data?.status === NEW_RESULTS_AVAILABLE && Array.isArray(data.company_ids)
        ? { status: NEW_RESULTS_AVAILABLE, ids: data.company_ids.map(String).filter(Boolean) }
        : { status: NEW_RESULTS_UNAVAILABLE, ids: [] });
    });
    return () => {
      cancelled = true;
    };
  }, [focus.status, savedSearchId]);

  const newCompanyIds = useMemo(
    () => (focus.status === NEW_RESULTS_AVAILABLE ? resolveNewCompanyIds(companies, focus.ids) : []),
    [companies, focus]
  );

  useEffect(() => {
    if (flashedRef.current || !ready || newCompanyIds.length === 0) return;
    flashedRef.current = true;
    setFlashNewResultRows(true);
  }, [newCompanyIds, ready]);

  useEffect(() => {
    if (!flashNewResultRows) return undefined;
    const timer = window.setTimeout(() => setFlashNewResultRows(false), NEW_RESULT_ROW_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashNewResultRows]);

  const orderedCompanies = useMemo(
    () => prioritizeNewCompanies(companies, newCompanyIds),
    [companies, newCompanyIds]
  );

  return { status: focus.status, newCompanyIds, flashNewResultRows, companies: orderedCompanies };
}
